import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, CheckCircle2 } from "lucide-react";

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [invite, setInvite] = useState<any>(null);
  const [orgName, setOrgName] = useState("");

  const token = searchParams.get("token");

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    if (!token) {
      toast({
        title: "Token inválido",
        description: "Link de convite inválido ou expirado.",
        variant: "destructive",
      });
      navigate("/auth");
      return;
    }

    try {
      // Buscar convite usando accepted_at como indicador (status não existe)
      const { data: inviteData, error } = await supabase
        .from("user_invites")
        .select(`
          *,
          orgs:org_id (name)
        `)
        .eq("token", token)
        .is("accepted_at", null)
        .single();

      if (error || !inviteData) {
        toast({
          title: "Convite não encontrado",
          description: "Este convite não existe ou já foi aceito.",
          variant: "destructive",
        });
        navigate("/auth");
        return;
      }

      if (new Date(inviteData.expires_at) < new Date()) {
        toast({
          title: "Convite expirado",
          description: "Este convite expirou. Solicite um novo convite.",
          variant: "destructive",
        });
        navigate("/auth");
        return;
      }

      setInvite(inviteData);
      setOrgName((inviteData.orgs as any)?.name || "");
      setValidating(false);
    } catch (error) {
      console.error("Erro ao validar token:", error);
      navigate("/auth");
    }
  };

  const handleAccept = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const password = formData.get("password") as string;
    const confirmPassword = formData.get("confirmPassword") as string;

    if (password !== confirmPassword) {
      toast({
        title: "Senhas não conferem",
        description: "As senhas devem ser iguais",
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      toast({
        title: "Senha muito curta",
        description: "A senha deve ter pelo menos 6 caracteres",
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    try {
      // Criar usuário no auth
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: invite.email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
        },
      });

      if (signUpError) throw signUpError;

      if (authData.user) {
        // Adicionar à organização
        const { error: memberError } = await supabase
          .from("org_members")
          .insert({
            org_id: invite.org_id,
            user_id: authData.user.id,
            role: invite.role,
          });

        if (memberError) throw memberError;

        // Marcar convite como aceito usando accepted_at
        await supabase
          .from("user_invites")
          .update({ accepted_at: new Date().toISOString() })
          .eq("id", invite.id);

        toast({
          title: "Conta criada com sucesso!",
          description: "Você será redirecionado em instantes...",
        });

        setTimeout(() => navigate("/dashboard"), 2000);
      }
    } catch (error: any) {
      console.error("Erro ao aceitar convite:", error);
      toast({
        title: "Erro ao criar conta",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <CheckCircle2 className="h-12 w-12 text-primary" />
          </div>
          <CardTitle className="text-2xl font-bold">Aceitar Convite</CardTitle>
          <CardDescription>
            Você foi convidado para fazer parte de <strong>{orgName}</strong>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAccept} className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={invite.email} disabled />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Criar Senha</Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="••••••••"
                required
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar Senha</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                placeholder="••••••••"
                required
                disabled={loading}
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Aceitar e Criar Conta
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
